import { createFileRoute, notFound } from '@tanstack/react-router';

import { HttpStatus } from '@mint-lib/api';
import { buildGuards, hasPermission } from '@mint-lib/routing-guards';
import { Campaign } from '@mint-sdk/api-campaign-collections';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { z } from 'zod';

import { isAxiosError } from '../../../../utils/helpers.js';

const guard = buildGuards(hasPermission('campaignTraffickingFeature:view'));

const handle404Error = (e: unknown) => {
  if (isAxiosError(e) && e.response?.status === HttpStatus.NotFound) {
    throw notFound();
  }
};

export const Route = createFileRoute(
  '/_secured/campaign/$campaignId/trafficking',
)({
  validateSearch: z.object({
    key: z.string().optional(),
    search: z.string().optional(),
    activityId: z.coerce.string().optional(),
  }),
  async beforeLoad({ context }) {
    const guardsContext = await context.getGuardContext(
      await context.getInstance('flagsmith', '@mint/arm'),
    );
    if (!guard(guardsContext)) {
      throw notFound();
    }
  },

  async loader({ context, params, location }) {
    const isOmniTrafficking = await context.isFlagEnabled(
      '@mint/arm',
      'omniTrafficking',
    );

    const i18n = await context.getInstance('i18n', '@mint/routing');

    await context.getInstance('i18n', '@mint-lib/trafficking');

    if (isOmniTrafficking) {
      await context.prefetchQuery(
        MediaPlan.getPreviewActivityListQueryOptions({
          params: {
            mediaPlanId: params.campaignId,
          },
        }),
      );

      const searchParams = new URLSearchParams(location.search);
      const activityId = searchParams.get('activityId');

      if (activityId) {
        try {
          await context.prefetchQuery(
            MediaPlan.getActivityById({
              urlParams: {
                activityId,
              },
              params: {
                mediaPlanId: params.campaignId,
              },
            }),
          );

          context.prefetchQuery(
            MediaPlan.getBudgetingLevelsList({
              urlParams: {
                activityId,
              },
            }),
          );
        } catch (e) {
          handle404Error(e);
        }
      }
    } else {
      try {
        await context.prefetchQuery(
          Campaign.getCampaignQueryOptions({
            urlParams: { campaignId: params.campaignId },
          }),
        );
      } catch (e) {
        handle404Error(e);
      }
    }

    const title = i18n.t('Trafficking | MINT');
    return { title };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.title,
      },
    ],
  }),
});
